import { useState } from "react";
import LocationPickerModal, {reverseGeocode} from "../LocationPickerModal";
import { useCreateLegalCase } from "../../../../hooks/useLegalQueries.js";
import {parseLatLng} from "../geo.js";

const CASE_TYPES = [
  "Accident",
  "Hit & Run",
  "Vehicle Seizure",
  "Insurance Dispute",
  "Traffic Violation",
  "Other",
];

const PRIORITY_OPTIONS = [
  { value: "LOW",      label: "Low" },
  { value: "MEDIUM",   label: "Medium" },
  { value: "HIGH",     label: "High" },
  { value: "CRITICAL", label: "Critical" },
];

const INITIAL_FORM = {
  vehicleNumber:      "",
  customerName:       "",   // as per RC
  customerPhone:      "",
  customerEmail:      "",
  incidentLocation:   "",
  accidentDate:       "",
  accidentTime:       "",
  caseType:           "",
  caseDescription:    "",
  authorityType:      "POLICE_STATION", // "POLICE_STATION" | "COURT"
  authorityName:      "",
  firNumber:          "",
  firCopyFile:        null,
  accidentMedia:      [],   // File[]
  injuryReported:     false,
  deathReported:      false,
  priority:           "MEDIUM",
  adminNotes:         "",
};

export default function CreateLegalCaseModal({ onClose = () => {}, onCreated = () => {}, prefill = null,accidentCaseNo = null, }) {
 const [form, setForm]         = useState({
    ...INITIAL_FORM,
    ...(prefill || {}),
    customerPhone: String(prefill?.customerPhone || "").replace(/\D/g, "").slice(-10),
  });
  const [errors, setErrors]     = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // ★ Incident location map picker — mirrors CreatecraneServiceModal's pattern
  const [incidentCoords, setIncidentCoords]         = useState(null);  // { lat, lng } | null
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // CHANGED: real mutation hook replaces local isPending state
  const createLegalCase = useCreateLegalCase();
  const isPending = createLegalCase.isPending;

  // ── Field helpers ──────────────────────────────────────────────────────────

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ★ Called when the user confirms a pin on the map picker
  const handleLocationConfirm = ({ address, coords }) => {
    set("incidentLocation", address);
    setIncidentCoords(coords || null);
    setShowLocationPicker(false);
  };

  const handleLocationBlur = async () => {
    const parsed = parseLatLng(form.incidentLocation);
    if (!parsed) return;
    const addr = await reverseGeocode(parsed.lat, parsed.lng);
    if (addr && addr !== "Unknown address") set("incidentLocation", addr);
  };

  const validate = () => {
    const errs = {};
    if (!form.vehicleNumber.trim())  errs.vehicleNumber  = "Vehicle number is required";
    if (!form.customerName.trim())   errs.customerName   = "Customer name is required";
    if (!form.customerPhone.trim())  errs.customerPhone  = "Phone number is required";
    else if (!/^\d{10}$/.test(form.customerPhone.trim()))
      errs.customerPhone = "Enter a valid 10-digit phone number";
    if (!form.accidentDate)          errs.accidentDate   = "Accident date is required";
    if (!form.authorityName.trim())
      errs.authorityName = form.authorityType === "COURT" ? "Court name is required" : "Police station is required";
    return errs;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (opts) => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
 
    setSubmitError(null);
    setIsDuplicate(false);
 
    const payload = {
      vehicleNumber:    form.vehicleNumber.trim().toUpperCase(),
      customerName:     form.customerName.trim(),
      customerPhone:    form.customerPhone.trim(),
      customerEmail:    form.customerEmail.trim() || undefined,
      incidentLocation: form.incidentLocation.trim() || undefined,
      incidentLat:      incidentCoords?.lat ?? undefined,
      incidentLng:      incidentCoords?.lng ?? undefined,
      accidentDate:     form.accidentDate,
      accidentTime:     form.accidentTime || undefined,
      caseType:         form.caseType || undefined,
      caseDescription:  form.caseDescription.trim() || undefined,
      authorityType:    form.authorityType,
      authorityName:    form.authorityName.trim(),
      firNumber:        form.firNumber.trim() || undefined,
      firCopyFile:      form.firCopyFile || undefined,    
      accidentMedia:    form.accidentMedia,        
      injuryReported:   form.injuryReported,
      deathReported:    form.deathReported,
      priority:         form.priority,
      adminNotes:       form.adminNotes.trim() || undefined,
      // ★ NEW: link to the accident case (undefined when launched standalone, so
      //        useCreateLegalCase skips it) + optional duplicate override
      accidentCaseNo:   accidentCaseNo || undefined,
      allowDuplicate:   opts?.allowDuplicate || undefined,
    };
 
    try {
    
      const data = await createLegalCase.mutateAsync(payload);
      onCreated?.(data);
      onClose();
    } catch (err) {
      console.error("Failed to create legal case", err);
      const backendMessage = err?.response?.data?.message;
      setSubmitError(backendMessage || "Something went wrong. Please try again.");
      // ★ NEW: remember if this was the duplicate-link rejection (HTTP 409 + code)
      setIsDuplicate(err?.response?.data?.code === "DUPLICATE_LINKED_SERVICE");
    }
  };
  // ── Render ─────────────────

  return (
    <>
    <div className="clm-overlay" onClick={(e) => e.target === e.currentTarget && !isPending && onClose()}>
      <div className="clm-modal">

        {/* Header */}
        <div className="clm-header">
          <div className="clm-header-left">
            <span className="clm-header-icon"><GavelIcon /></span>
            <div>
              <h2 className="clm-title">New Legal / Accident Case</h2>
              <p className="clm-subtitle">
                {accidentCaseNo
                  ? <>Linked to case <strong style={{ fontFamily: "var(--wd-mono)" }}>{accidentCaseNo}</strong></>
                  : "Start a new legal case for a vehicle accident"}
              </p>
            </div>
          </div>
          <button className="clm-close" onClick={onClose} disabled={isPending}>
            <CloseIcon />
          </button>
        </div>

        <div className="clm-body">

          {/* Section: Vehicle & Customer */}
          <SectionLabel label="Vehicle & Customer" />
          <div className="clm-grid clm-grid-2">
            <Field label="Vehicle Number *" error={errors.vehicleNumber}>
              <input
                className={`clm-input clm-input--mono ${errors.vehicleNumber ? "clm-input--error" : ""}`}
                placeholder="e.g. HR46E8144"
                value={form.vehicleNumber}
                onChange={(e) => set("vehicleNumber", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Case Type">
              <select
                className="clm-input clm-select"
                value={form.caseType}
                onChange={(e) => set("caseType", e.target.value)}
              >
                <option value="">Select case type</option>
                {CASE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Customer Name (as per RC) *" error={errors.customerName}>
              <input
                className={`clm-input ${errors.customerName ? "clm-input--error" : ""}`}
                placeholder="Full name"
                value={form.customerName}
                onChange={(e) => set("customerName", e.target.value)}
              />
            </Field>
            <Field label="Customer Mobile Number *" error={errors.customerPhone}>
              <input
                className={`clm-input clm-input--mono ${errors.customerPhone ? "clm-input--error" : ""}`}
                placeholder="10-digit mobile number"
                value={form.customerPhone}
                onChange={(e) => set("customerPhone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                maxLength={10}
              />
            </Field>
            <Field label="Customer Email">
              <input
                className="clm-input"
                type="email"
                placeholder="Optional"
                value={form.customerEmail}
                onChange={(e) => set("customerEmail", e.target.value)}
              />
            </Field>
            <Field label="Incident Location">
              <div className="clm-location-row">
                <input
                  className="clm-input"
                  placeholder="Search or pin on map"
                  value={form.incidentLocation}
                  onChange={(e) => {
                    const v = e.target.value;
                    set("incidentLocation", v);
                    setIncidentCoords(parseLatLng(v));
                  }} 
                onBlur={handleLocationBlur}               />
                {incidentCoords && (
                  <span
                    className="clm-coords-badge"
                    title={`${incidentCoords.lat.toFixed(5)}, ${incidentCoords.lng.toFixed(5)}`}
                  >
                    <PinIcon /> Pinned
                  </span>
                )}
                <button
                  type="button"
                  className="clm-map-btn"
                  onClick={() => setShowLocationPicker(true)}
                  title="Pick on map"
                >
                  <MapIcon />
                </button>
              </div>
            </Field>
          </div>

          {/* Section: Accident Details */}
          <SectionLabel label="Accident Details" />
          <div className="clm-grid clm-grid-2">
            <Field label="Accident Date *" error={errors.accidentDate}>
              <input
                className={`clm-input ${errors.accidentDate ? "clm-input--error" : ""}`}
                type="date"
                value={form.accidentDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => set("accidentDate", e.target.value)}
              />
            </Field>
            <Field label="Accident Time">
              <input
                className="clm-input"
                type="time"
                value={form.accidentTime}
                onChange={(e) => set("accidentTime", e.target.value)}
              />
            </Field>
            <Field label="Case Description" className="clm-col-span-2">
              <textarea
                className="clm-input clm-textarea"
                placeholder="Brief description of how the accident happened"
                rows={3}
                value={form.caseDescription}
                onChange={(e) => set("caseDescription", e.target.value)}
              />
            </Field>
          </div>

          {/* Section: FIR / Authority */}
          <SectionLabel label="FIR & Authority" />
          <Field label="Reported To">
            <div className="clm-authority-toggle">
              <button
                type="button"
                className={`clm-authority-btn ${form.authorityType === "POLICE_STATION" ? "clm-authority-btn--active" : ""}`}
                onClick={() => set("authorityType", "POLICE_STATION")}
              >
                Police Station
              </button>
              <button
                type="button"
                className={`clm-authority-btn ${form.authorityType === "COURT" ? "clm-authority-btn--active" : ""}`}
                onClick={() => set("authorityType", "COURT")}
              >
                Court
              </button>
            </div>
          </Field>
          <div className="clm-grid clm-grid-2">
            <Field
              label={(form.authorityType === "COURT" ? "Court Name" : "Police Station Name") + " *"}
              error={errors.authorityName}
            >
              <input
                className={`clm-input ${errors.authorityName ? "clm-input--error" : ""}`}
                placeholder={form.authorityType === "COURT" ? "Court name" : "Station name"}
                value={form.authorityName}
                onChange={(e) => set("authorityName", e.target.value)}
              />
            </Field>
            <Field label="FIR Number">
              <input
                className="clm-input"
                placeholder="If available"
                value={form.firNumber}
                onChange={(e) => set("firNumber", e.target.value)}
              />
            </Field>
            <Field label="FIR Copy">
              <FileZone
                file={form.firCopyFile}
                accept="application/pdf,image/*"
                onChange={(f) => set("firCopyFile", f)}
              />
            </Field>
            <Field label="Accident Photos / Videos">
              <FileZone
                multiple
                files={form.accidentMedia}
                accept="image/*,video/*"
                onChange={(files) => set("accidentMedia", files)}
              />
            </Field>
          </div>

          {/* Section: Priority + Flags */}
          <SectionLabel label="Severity & Flags" />
          <Field label="Priority">
            <div className="clm-priority-row">
              {PRIORITY_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`clm-priority-btn clm-priority-btn--${value.toLowerCase()} ${form.priority === value ? "clm-priority-btn--active" : ""}`}
                  onClick={() => set("priority", value)}
                >
                  <span className="clm-priority-dot" data-level={value.toLowerCase()} />
                  {label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Incident Flags">
            <div className="clm-flag-row">
              <button
                type="button"
                className={`clm-flag-card ${form.injuryReported ? "clm-flag-card--injury clm-flag-card--on" : ""}`}
                onClick={() => set("injuryReported", !form.injuryReported)}
              >
                <span className="clm-flag-icon">&#129657;</span>
                <span className="clm-flag-text">Injury reported</span>
                <span className={`clm-flag-toggle ${form.injuryReported ? "clm-flag-toggle--on" : ""}`} />
              </button>
              <button
                type="button"
                className={`clm-flag-card ${form.deathReported ? "clm-flag-card--death clm-flag-card--on" : ""}`}
                onClick={() => set("deathReported", !form.deathReported)}
              >
                <span className="clm-flag-icon">&#128680;</span>
                <span className="clm-flag-text">Death reported</span>
                <span className={`clm-flag-toggle ${form.deathReported ? "clm-flag-toggle--on clm-flag-toggle--danger" : ""}`} />
              </button>
            </div>
          </Field>

          {/* Section: Notes */}
          <SectionLabel label="Internal Notes" />
          <Field label="Admin Notes">
            <textarea
              className="clm-input clm-textarea"
              placeholder="Internal notes (not visible to customer)"
              rows={2}
              value={form.adminNotes}
              onChange={(e) => set("adminNotes", e.target.value)}
            />
          </Field>

          {submitError && (
            <div className="clm-submit-error">
              {submitError}
              {/* ★ NEW: lets the admin override the duplicate check on purpose */}
              {isDuplicate && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="clm-btn clm-btn--secondary"
                    onClick={() => handleSubmit({ allowDuplicate: true })}
                    disabled={isPending}
                  >
                    Create anyway
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="clm-footer">
          <button className="clm-btn clm-btn--secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button className="clm-btn clm-btn--primary" onClick={() => handleSubmit()} disabled={isPending}>
            {isPending ? (
              <>
                <span className="clm-spinner" />
                Creating…
              </>
            ) : (
              <>
                <GavelIcon />
                Create Case
              </>
            )}
          </button>
        </div>
      </div>

      <style>{MODAL_CSS}</style>
    </div>

    {showLocationPicker && (
      <LocationPickerModal
        field="pickup"
        title="Set incident location"
        initialAddress={form.incidentLocation}
        onConfirm={handleLocationConfirm}
        onClose={() => setShowLocationPicker(false)}
      />
    )}
    </>
  );
}

/* ── Sub-components ── */
function SectionLabel({ label }) {
  return <div className="clm-section-label">{label}</div>;
}

function Field({ label, error, children, className = "" }) {
  return (
    <div className={`clm-field ${className}`}>
      <label className="clm-label">{label}</label>
      {children}
      {error && <span className="clm-error-msg">{error}</span>}
    </div>
  );
}

// Single or multi file picker with a lightweight preview strip
function FileZone({ file, files, multiple, accept, onChange }) {
  const list = multiple ? (files || []) : (file ? [file] : []);

  const handleChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (multiple) {
      onChange([...(files || []), ...picked]);
    } else {
      onChange(picked[0] || null);
    }
    e.target.value = ""; // allow re-picking the same file
  };

  const removeAt = (idx) => {
    if (multiple) {
      onChange((files || []).filter((_, i) => i !== idx));
    } else {
      onChange(null);
    }
  };

  const fmtSize = (b) => (b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <div className="clm-filezone">
      <label className="clm-filezone-btn">
        <input type="file" accept={accept} multiple={multiple} onChange={handleChange} style={{ display: "none" }} />
        <span>{list.length ? "＋ Add more" : "↑ Choose file"}</span>
      </label>
      {list.length > 0 && (
        <div className="clm-filezone-list">
          {list.map((f, i) => (
            <div key={`${f.name}-${i}`} className="clm-filezone-chip">
              <span className="clm-filezone-chip-name">{f.name}</span>
              <span className="clm-filezone-chip-size">{fmtSize(f.size)}</span>
              <button type="button" className="clm-filezone-chip-remove" onClick={() => removeAt(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Icons ── */
function GavelIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m14 13-8.5 8.5a2.12 2.12 0 0 1-3-3L11 10"/>
      <path d="m16 16 6-6"/><path d="m8 8 6-6"/><path d="m9 7 8 8"/>
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function MapIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

/* ── Styles ── */
const MODAL_CSS = `
.clm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: clm-fade-in 0.15s ease;
}

@keyframes clm-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.clm-modal {
  background: #161e2e;
  border: 1px solid #243044;
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.04);
  overflow: hidden;
  animation: clm-slide-up 0.18s ease;
}

@keyframes clm-slide-up {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.clm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 16px;
  border-bottom: 1px solid #1e2c42;
  flex-shrink: 0;
  background: #111827;
}
.clm-header-left { display: flex; align-items: center; gap: 12px; }
.clm-header-icon {
  width: 36px; height: 36px; border-radius: 8px;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #60a5fa;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.clm-title { font-size: 15px; font-weight: 600; color: #f1f5f9; margin: 0; letter-spacing: -0.01em; }
.clm-subtitle { font-size: 12px; color: #64748b; margin: 2px 0 0; }
.clm-close {
  width: 30px; height: 30px; border: none; background: transparent; cursor: pointer;
  border-radius: 6px; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s; flex-shrink: 0;
}
.clm-close:hover:not(:disabled) { background: #1e2c42; color: #cbd5e1; }
.clm-close:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── Body ── */
.clm-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  scrollbar-width: thin;
  scrollbar-color: #243044 transparent;
}
.clm-body::-webkit-scrollbar { width: 5px; }
.clm-body::-webkit-scrollbar-track { background: transparent; }
.clm-body::-webkit-scrollbar-thumb { background: #243044; border-radius: 3px; }

/* ── Section labels ── */
.clm-section-label {
  font-size: 11.5px; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase;
  color: #979fac; margin: 16px 0 10px; padding-bottom: 7px; border-bottom: 1px solid #1e2c42;
}
.clm-section-label:first-child { margin-top: 0; }

/* ── Grid ── */
.clm-grid { display: grid; gap: 12px; align-items: start; }
.clm-grid-2 { grid-template-columns: 1fr 1fr; }
.clm-col-span-2 { grid-column: span 2; }

/* ── Fields ── */
.clm-field { display: flex; flex-direction: column; gap: 7px; text-align: left; }
.clm-label {
  font-size: 11.5px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.01em;
  text-align: left;
}

/* ── Inputs ── */
.clm-input {
  height: 36px; padding: 0 11px; border: 1px solid #243044; border-radius: 7px;
  font-size: 13px; color: #e2e8f0; background: #0f1623; outline: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  width: 100%; box-sizing: border-box; font-family: inherit; text-align: left;
}
.clm-input::placeholder { color: #374f6b; }
.clm-input:focus {
  border-color: #3b82f6; background: #0d1520; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}
.clm-input:hover:not(:focus):not(.clm-input--error) { border-color: #2d3f58; }
.clm-input--error { border-color: #f87171; background: rgba(248, 113, 113, 0.04); }
.clm-input--error:focus { box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.12); }
.clm-input--mono { font-family: 'Courier New', Courier, monospace; letter-spacing: 0.06em; }

.clm-select {
  cursor: pointer;
  appearance: none; -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 7L11 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
  background-color: #0f1623; padding-right: 30px;
}
.clm-select option { background: #161e2e; color: #e2e8f0; }
.clm-input[type="date"], .clm-input[type="time"] { color-scheme: dark; }

.clm-textarea { height: auto; padding: 9px 11px; resize: vertical; min-height: 60px; line-height: 1.5; }

.clm-error-msg { font-size: 11px; color: #f87171; display: flex; align-items: center; gap: 4px; }
.clm-submit-error {
  margin-top: 12px; padding: 10px 12px; border-radius: 8px; font-size: 12px;
  background: rgba(248, 113, 113, 0.08); border: 1px solid rgba(248, 113, 113, 0.25); color: #f87171;
}

/* ── Location field with map picker ── */
.clm-location-row { display: flex; align-items: center; gap: 6px; width: 100%; }
.clm-map-btn {
  flex-shrink: 0; width: 36px; height: 36px;
  border: 1px solid #243044; border-radius: 7px;
  background: #0f1623; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.clm-map-btn:hover { border-color: #3b82f6; color: #60a5fa; background: rgba(59, 130, 246, 0.08); }
.clm-coords-badge {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 500;
  color: #3b82f6; background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 20px; padding: 2px 7px;
  white-space: nowrap; flex-shrink: 0;
}

/* ── Authority toggle ── */
.clm-authority-toggle { display: flex; gap: 6px; }
.clm-authority-btn {
  flex: 1; height: 32px; border-radius: 7px; font-size: 12px; font-weight: 500;
  cursor: pointer; border: 1px solid #243044; background: #0f1623; color: #64748b;
  transition: all 0.15s;
}
.clm-authority-btn:hover:not(.clm-authority-btn--active) { border-color: #2d3f58; color: #94a3b8; }
.clm-authority-btn--active {
  background: rgba(37, 99, 235, 0.14); color: #60a5fa; border-color: rgba(59,130,246,0.4);
}

/* ── File zone ── */
.clm-filezone { display: flex; flex-direction: column; gap: 6px; }
.clm-filezone-btn {
  display: inline-flex; align-items: center; justify-content: center;
  height: 32px; padding: 7px 9px; border-radius: 7px; font-size: 12px; font-weight: 500;
  cursor: pointer; border: 1px dashed #2d3f58; background: #0f1623; color: #94a3b8;
  transition: all 0.15s; width: fit-content; box-sizing: border-box;
}
.clm-filezone-btn:hover { border-color: #3b82f6; color: #60a5fa; background: #0d1520; }
.clm-filezone-btn:has(input:focus-visible) {
  border-color: #3b82f6; border-style: solid; color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}
.clm-filezone-list { display: flex; flex-direction: column; gap: 5px; }
.clm-filezone-chip {
  display: flex; align-items: center; gap: 8px;
  padding: 5px 9px; border-radius: 6px;
  background: rgba(99,102,241,.07); border: 1px solid rgba(99,102,241,.2);
  font-size: 11px;
}
.clm-filezone-chip-name {
  flex: 1; color: #a5b4fc; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.clm-filezone-chip-size { color: #64748b; flex-shrink: 0; }
.clm-filezone-chip-remove {
  background: none; border: none; cursor: pointer; color: #64748b; font-size: 11px; padding: 2px 4px; flex-shrink: 0;
}
.clm-filezone-chip-remove:hover { color: #f87171; }

/* ── Priority pills ── */
.clm-priority-row { display: flex; gap: 6px; flex-wrap: wrap; padding-top: 2px; }
.clm-priority-btn {
  height: 28px; padding: 0 13px; border-radius: 20px; font-size: 11.5px; font-weight: 500;
  cursor: pointer; border: 1px solid #243044; transition: all 0.15s;
  background: #0f1623; color: #64748b; letter-spacing: 0.01em;
  display: inline-flex; align-items: center; gap: 6px;
}
.clm-priority-btn:hover:not(.clm-priority-btn--active) {
  border-color: #2d3f58; color: #94a3b8; background: #141d2d;
}
.clm-priority-btn--low.clm-priority-btn--active {
  background: rgba(16, 185, 129, 0.12); color: #34d399; border-color: rgba(52, 211, 153, 0.35);
}
.clm-priority-btn--medium.clm-priority-btn--active {
  background: rgba(251, 191, 36, 0.12); color: #fbbf24; border-color: rgba(251, 191, 36, 0.35);
}
.clm-priority-btn--high.clm-priority-btn--active {
  background: rgba(251, 146, 60, 0.12); color: #fb923c; border-color: rgba(251, 146, 60, 0.35);
}
.clm-priority-btn--critical.clm-priority-btn--active {
  background: rgba(248, 113, 113, 0.14); color: #f87171; border-color: rgba(248, 113, 113, 0.35);
}
.clm-priority-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.clm-priority-btn--low      .clm-priority-dot { background: #34d399; }
.clm-priority-btn--medium   .clm-priority-dot { background: #fbbf24; }
.clm-priority-btn--high     .clm-priority-dot { background: #fb923c; }
.clm-priority-btn--critical .clm-priority-dot { background: #f87171; }

/* ── Flag toggle cards ── */
.clm-flag-row { display: flex; gap: 10px; }
.clm-flag-card {
  flex: 1; display: flex; align-items: center; gap: 10px;
  padding: 11px 14px; background: #0f1623; border: 1px solid #243044; border-radius: 8px;
  cursor: pointer; transition: border-color 0.15s, background 0.15s; text-align: left; font-family: inherit;
}
.clm-flag-card:hover:not(.clm-flag-card--on) { border-color: #2d3f58; background: #111d2e; }
.clm-flag-icon { font-size: 16px; flex-shrink: 0; line-height: 1; }
.clm-flag-text { flex: 1; font-size: 13px; color: #64748b; font-weight: 500; transition: color 0.15s; }
.clm-flag-card--on .clm-flag-text { color: #e2e8f0; }

.clm-flag-toggle {
  width: 30px; height: 17px; border-radius: 10px; background: #1e2c42; border: 1px solid #2d3f58;
  position: relative; flex-shrink: 0; transition: background 0.2s, border-color 0.2s;
}
.clm-flag-toggle::after {
  content: ''; position: absolute; top: 2px; left: 2px; width: 11px; height: 11px; border-radius: 50%;
  background: #475569; transition: transform 0.2s, background 0.2s;
}
.clm-flag-toggle--on { background: #1d4ed8; border-color: #3b82f6; }
.clm-flag-toggle--on::after { transform: translateX(13px); background: #fff; }
.clm-flag-toggle--danger.clm-flag-toggle--on { background: #991b1b; border-color: #f87171; }

.clm-flag-card--injury.clm-flag-card--on { border-color: rgba(59,130,246,0.4); background: rgba(29,78,216,0.08); }
.clm-flag-card--death.clm-flag-card--on { border-color: rgba(248,113,113,0.4); background: rgba(153,27,27,0.1); }

/* ── Footer ── */
.clm-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 14px 24px; border-top: 1px solid #1e2c42; flex-shrink: 0; background: #111827;
}
.clm-btn {
  display: inline-flex; align-items: center; gap: 6px; height: 35px; padding: 0 16px;
  border-radius: 7px; font-size: 13px; font-weight: 500; cursor: pointer; border: none;
  transition: all 0.15s; white-space: nowrap; font-family: inherit; letter-spacing: 0.01em;
}
.clm-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.clm-btn--secondary { background: #1a2538; color: #94a3b8; border: 1px solid #243044; }
.clm-btn--secondary:hover:not(:disabled) { background: #1e2c42; color: #cbd5e1; border-color: #2d3f58; }
.clm-btn--primary { background: #2563eb; color: #fff; border: 1px solid transparent; }
.clm-btn--primary:hover:not(:disabled) { background: #1d4ed8; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2); }

/* ── Spinner ── */
.clm-spinner {
  width: 13px; height: 13px; border: 2px solid rgba(255, 255, 255, 0.25); border-top-color: #fff;
  border-radius: 50%; animation: clm-spin 0.65s linear infinite; flex-shrink: 0;
}
@keyframes clm-spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (max-width: 600px) {
  .clm-grid-2    { grid-template-columns: 1fr; }
  .clm-col-span-2 { grid-column: span 1; }
  .clm-modal     { max-height: 95vh; border-radius: 10px; }
  .clm-header,
  .clm-footer    { padding-left: 16px; padding-right: 16px; }
  .clm-body      { padding: 16px; }
}
`;